import React, { useCallback, useMemo, useState } from 'react'
import { useTheme } from '@emotion/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { isMobile } from 'react-device-detect'
import {
  CartItem,
  MenuItemFavorite,
  MenuItem as MenuItemType,
} from '@open-tender/types'
import {
  makeOrderItem,
  rehydrateOrderItem,
  slugify,
  useOrderItem,
} from '@open-tender/utils'
import {
  addItemToCart,
  openModal,
  selectCartCounts,
  selectDisplaySettings,
  selectMenu,
  selectMenuSlug,
  selectPointsProgram,
  setCurrentItem,
  showNotification,
  setMenuPath,
  toggleSidebarModal,
  useAppDispatch,
  useAppSelector,
  setOffsetTop,
  selectSelectedAllergenNames,
  selectSelectedTagNames,
  selectBrand,
  selectOrder,
} from '@open-tender/cloud'
import {
  Button,
  MenuItemButton,
  MenuItemOverlay,
  MenuItemTagAlert,
} from 'components'
import MenuItemCount from '../MenuItemCount'
import {
  MenuItemButtons,
  MenuItemButtonsAdd,
  MenuItemButtonsContainer,
  MenuItemButtonsCustomize,
  MenuItemButtonsQuickAdd,
  MenuItemButtonsWarning,
  MenuItemView,
  MenuItemWrapper,
} from './MenuItem.styled'
import { Plus } from 'components/icons'
import MenuItemUpsell from '../../../MenuItem/MenuItemUpsell'
import {
  getDisplayPriceForPerPerson,
  DisplaySettingsWithPricePerPerson,
  shouldShowPricePerPerson,
} from 'utils/pricePerPerson'
import { correctRemovalModifierCals } from 'utils/removalModifierCals'
import {
  clearSlots,
  BrandWithAssortment,
  isAssortmentEnabled,
  isAssortmentItem,
} from 'utils/assortment'

type ItemWithServes = MenuItemType & { serves?: number }

const MenuItem = ({
  item,
  favorite,
  displayOnly = false,
  addCallback,
}: {
  item: MenuItemType
  favorite?: MenuItemFavorite
  displayOnly?: boolean
  addCallback?: () => void
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [clicked, setClicked] = useState(false)
  const [showUpsell, setShowUpsell] = useState<boolean>(false)
  const [isUpsellMounted, setIsUpsellMounted] = useState<boolean>(false)
  const { pathname } = useLocation()
  const { requestedAt, orderType } = useAppSelector(selectOrder)
  const brand = useAppSelector(selectBrand)
  const menuSlug = useAppSelector(selectMenuSlug)
  const cartCounts = useAppSelector(selectCartCounts)
  const { soldOut } = useAppSelector(selectMenu)
  const pointsProgram = useAppSelector(selectPointsProgram)
  const hasPoints = !!pointsProgram
  const theme = useTheme()
  const { buttons, content, image } = theme.menuItems
  const buttonsDisplay = isMobile ? buttons.displayMobile : buttons.display
  const showButtons = buttonsDisplay === 'SHOW'
  const showQuickAdd = buttonsDisplay === 'MINIMAL'
  const quickAddSize = isMobile ? 20 : 24
  const showDesc = isMobile ? content.descriptionMobile : content.description
  const showImage = image.position !== 'HIDDEN'
  const displaySettings = useAppSelector(selectDisplaySettings)
  const allergenAlerts = useAppSelector(selectSelectedAllergenNames)
  const tagAlerts = useAppSelector(selectSelectedTagNames)
  const {
    calories: showCals = false,
    tags: showTags = false,
    allergens: showAllergens = false,
  } = displaySettings || {}
  const orderItem = favorite
    ? {
        ...rehydrateOrderItem(item, favorite.item, soldOut, requestedAt),
        index: -1,
      }
    : makeOrderItem(item, undefined, soldOut, undefined, hasPoints, requestedAt)

  // Display-only: add removal-modifier calories to the listing card so the grid
  // matches the detail page (base + Σ removals). The raw orderItem still flows to
  // cart / currentItem unchanged; the detail builder re-applies this correction.
  const correctedItem = correctRemovalModifierCals(orderItem as CartItem)

  const hasUpsell = item.upsell_items.length > 0

  const {
    name,
    imageUrl,
    displayDesc,
    displayTags,
    displayAllergens,
    displayPrice,
    displayCals,
    isIncomplete,
    isSoldOut,
    allergenAlert,
    sizeOnly,
    cartCount,
    tagAlert,
  } = useOrderItem(
    correctedItem,
    favorite,
    allergenAlerts,
    cartCounts,
    showCals,
    showTags,
    showAllergens,
    showDesc,
    tagAlerts
  )
  const { builderType } = displaySettings || {}
  const adjBuilderType = isMobile ? 'PAGE' : builderType
  const addDisabled = isIncomplete || isSoldOut
  const customizeIsPrimary = addDisabled && !isSoldOut
  const disabled = isSoldOut || displayOnly
  const showHover = !disabled && !isMobile

  const itemWithServes = item as ItemWithServes
  const priceDollars = itemWithServes.price
  const serves = itemWithServes.serves
  const showPricePerPerson = shouldShowPricePerPerson(
    orderType,
    displaySettings as DisplaySettingsWithPricePerPerson
  )
  const priceToShow = useMemo(
    () =>
      getDisplayPriceForPerPerson({
        baseDisplayPrice: displayPrice ?? '',
        priceDollars,
        serves,
        showPricePerPerson,
      }),
    [displayPrice, priceDollars, serves, showPricePerPerson]
  )

  // Catering assortments open the builder screen for both buttons: "Add To
  // Order" pre-filled with the menu defaults, "Customize" with empty slots
  // (a saved favorite keeps its own selections). The full-page builder has no
  // assortment screen.
  const isAssortment =
    adjBuilderType !== 'PAGE_FULL' &&
    isAssortmentItem(
      orderItem as CartItem,
      isAssortmentEnabled(brand as BrandWithAssortment, orderType)
    )

  const openItem = (itemToOpen: typeof orderItem) => {
    if (!isSoldOut) {
      dispatch(setMenuPath(pathname || menuSlug))
      dispatch(setCurrentItem(itemToOpen))
      if (adjBuilderType?.includes('PAGE')) {
        const scrollTop =
          document.documentElement.scrollTop || document.body.scrollTop || null
        dispatch(setOffsetTop(scrollTop))
        navigate(`${menuSlug}/item/${slugify(name ?? '')}`)
      } else if (adjBuilderType === 'SIDEBAR') {
        dispatch(toggleSidebarModal())
      } else {
        dispatch(openModal({ type: 'item', args: { focusFirst: true } }))
      }
    }
  }

  const view = () => openItem(orderItem)

  const viewEmpty = () =>
    openItem(clearSlots(orderItem as CartItem) as typeof orderItem)

  const handleOnShowUpsells = () => {
    setIsUpsellMounted(true)
    setTimeout(() => setShowUpsell(true))
  }

  const handleOnHideUpsells = useCallback(() => {
    setShowUpsell(false)
    setTimeout(() => setIsUpsellMounted(false), 250)
  }, [])

  const add = () => {
    if (!isSoldOut) {
      const cartItem = { ...orderItem } as CartItem
      if (cartItem.index === -1) delete cartItem.index
      dispatch(addItemToCart(cartItem))
      dispatch(showNotification(`${name} added to cart!`))
      if (addCallback) addCallback()
      if (hasUpsell) handleOnShowUpsells()
    }
  }

  const imageOverlay = showImage ? (
    <MenuItemOverlay
      isSoldOut={isSoldOut}
      suspendedUntil={item.suspend_until}
      allergenAlert={allergenAlert ?? undefined}
      tagAlert={tagAlert ?? undefined}
    />
  ) : null

  return (
    <>
      <MenuItemWrapper>
        {cartCount > 0 && (
          <MenuItemCount>
            <span>{cartCount}</span>
          </MenuItemCount>
        )}
        {!showImage ? (
          <MenuItemTagAlert
            isSoldOut={isSoldOut}
            allergenAlert={allergenAlert ?? ''}
          />
        ) : null}
        <MenuItemView className={showHover ? 'show-hover' : ''}>
          {showQuickAdd && !addDisabled ? (
            <MenuItemButtonsQuickAdd>
              <Button
                onClick={
                  isAssortment
                    ? view
                    : isIncomplete
                    ? () => setClicked(true)
                    : add
                }
                size="small"
                color={buttons.color}
                disabled={addDisabled}
              >
                <Plus size={quickAddSize} />
              </Button>
            </MenuItemButtonsQuickAdd>
          ) : null}
          <MenuItemButton
            onClick={view}
            disabled={disabled}
            imageUrl={imageUrl ?? ''}
            imageOverlay={imageOverlay}
            name={item.name ?? ''}
            desc={displayDesc}
            price={priceToShow}
            cals={displayCals ?? ''}
            tags={displayTags}
            allergens={displayAllergens}
          />
          {showButtons && (
            <MenuItemButtons>
              {clicked && (
                <MenuItemButtonsWarning>
                  Item requires customization. Tap "Customize".
                </MenuItemButtonsWarning>
              )}
              <MenuItemButtonsContainer>
                <MenuItemButtonsAdd disabled={isSoldOut}>
                  <Button
                    onClick={isIncomplete || isAssortment ? view : add}
                    size="small"
                    color={buttons.color}
                    disabled={isSoldOut}
                  >
                    Add To Order
                  </Button>
                </MenuItemButtonsAdd>
                <MenuItemButtonsCustomize
                  customizeIsPrimary={customizeIsPrimary}
                >
                  <Button
                    onClick={isAssortment && !favorite ? viewEmpty : view}
                    disabled={isSoldOut}
                    size="small"
                    color="secondary"
                  >
                    {sizeOnly ? 'Choose Size' : 'Customize'}
                  </Button>
                </MenuItemButtonsCustomize>
              </MenuItemButtonsContainer>
            </MenuItemButtons>
          )}
        </MenuItemView>
      </MenuItemWrapper>
      {isUpsellMounted && (
        <MenuItemUpsell
          initiatingMenuItem={orderItem as CartItem}
          showUpsell={showUpsell}
          upsellItemIds={item.upsell_items}
          cancel={handleOnHideUpsells}
        />
      )}
    </>
  )
}

export default MenuItem
